import React, { useEffect, useState, lazy, Suspense } from 'react'
import {
  AppBar as MuiAppBar, Box, Toolbar, IconButton, Typography, Menu, MenuItem,
  Drawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, CssBaseline, Divider, Avatar, Tooltip,
  Collapse
} from '@mui/material'

import MenuIcon from '@mui/icons-material/Menu';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Logout from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CampaignIcon from '@mui/icons-material/Campaign';
import NightShelterIcon from '@mui/icons-material/NightShelter';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate
} from "react-router-dom"
import { styled, useTheme } from '@mui/material/styles'
import { useNavigate } from "react-router-dom"

import {
  useUserContext,
  // RoleCheckWrapper, 
  useMuniContext
} from 'strview-common'
import { general } from 'strview-common'
import { getConfig } from './munis/muni-config'
// import {useMuniContext} from '../external/muni-context'

// import Dashboard from '../pages/auth/dashboard'
// import Welcome from '../pages/auth/welcome'
// import SubmitComplaint from '../pages/auth/submit-complaint'
// import ComplaintList from '../pages/auth/complaint-list'

// Lazy loading
const Dashboard = lazy(() => import('../pages/auth/dashboard'))
const Welcome = lazy(() => import('../pages/auth/welcome'))
const SubmitComplaint = lazy(() => import('../pages/auth/submit-complaint'))
const ComplaintList = lazy(() => import('../pages/auth/complaint-list'))
const SubmitPermitApplication = lazy(() => import('../pages/auth/submit-permit-application'))
const PermitApplicationList = lazy(() => import('../pages/auth/permit-application-list'))
const PermitList = lazy(() => import('../pages/auth/permit-list'))
const VoucherList = lazy(() => import('../pages/auth/voucher-list'))
const SmartForm = lazy(() => import('../pages/auth/smart-form'))
const MuniciPayReturn = lazy(() => import('./municipay/municipay-return'))

const { ModalLayout, DataDisplayer } = general


const drawerWidth = 240

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
)

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}))

const LogoutItem = ({ signOut }) => {
  const navigate = useNavigate()
  let loginUrl = "/login"
  const logout = () => {
    const tempUser = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null
    if (tempUser && tempUser.userType === "google") {
      loginUrl = loginUrl + "?federatedLogout=true"
    }
    signOut()
    navigate(loginUrl)
  }
  return (
    <MenuItem onClick={logout}>
      <ListItemIcon>
        <Logout fontSize="small" />
      </ListItemIcon>
      Logout
    </MenuItem>
  )
}

const MyProfileItem = () => {
  const { getUserProfile } = useUserContext()
  const [open, setOpen] = useState(false)
  const [userProfile, setUserProfile] = useState(null)

  const handleOpen = (event) => {
    event.stopPropagation()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const getUserProfileData = async () => {
    const data = getUserProfile()
    console.log(data)
    // let's only get certain fields from the user profile
    const fields = ["email", "username", "userType", "apps", "roles", "munis", "lastTokenRefresh"]
    const filteredData = {}
    fields.forEach(field => {
      filteredData[field] = data[field]
    })
    setUserProfile(filteredData)
  }

  useEffect(() => {
    getUserProfileData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <MenuItem onClick={handleOpen}>
        <ListItemIcon>
          <Avatar /> My profile
        </ListItemIcon>
      </MenuItem>
      <ModalLayout open={open} onClose={handleClose}>
        <DataDisplayer data={userProfile} width="100%" readableKey={true} size="large" />
      </ModalLayout>
    </>
  )


}

const LayoutAuth = ({ mode, setMode }) => {
  const { signOut } = useUserContext()
  const { getMuniId } = useMuniContext()
  const muniId = getMuniId()
  const config = getConfig(muniId)
  const theme = useTheme()
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = React.useState(null)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [pageTitle, setPageTitle] = useState("Dashboard")
  const [menuOpen, setMenuOpen] = useState({})
  const [env, setEnv] = useState("dev")
  const accountMenuOpen = Boolean(accountMenuAnchorEl)

  useEffect(() => {
    setEnv(process.env.REACT_APP_ENV)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMenuClick = (name) => {
    setMenuOpen(prevState => ({ ...prevState, [name]: !prevState[name] }));
  };

  const handleAccountMenuClick = (event) => {
    setAccountMenuAnchorEl(event.currentTarget)
  }
  const handleAccountMenuClose = () => {
    setAccountMenuAnchorEl(null)
  }

  const storeUserSetPreference = (pref) => {
    localStorage.setItem("mode", pref)
  }

  const getUserSetPreference = () => {
    return localStorage.getItem("mode")
  }

  const toggleMode = () => {
    const newMode = mode === "light" ? "dark" : "light"
    setMode(newMode)
    storeUserSetPreference(newMode)
  }

  useEffect(() => {
    const userPreference = getUserSetPreference()
    if (userPreference) {
      setMode(userPreference)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawerOpen = () => {
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
  }

  const handleListItemClick = (event, index, pageTitle) => {
    setSelectedIndex(index)
    setPageTitle(pageTitle)
    // if(mobileOpen) setMobileOpen(!mobileOpen)
  }

  return (
    <Router>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" open={drawerOpen} style={{ backgroundColor: theme.palette.background.appBar }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(drawerOpen && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {pageTitle} {env !== 'prod' && `*** ${env.toUpperCase()} ***`}
            </Typography>
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleAccountMenuClick}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={drawerOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={drawerOpen ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32 }}>M</Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={accountMenuAnchorEl}
              id="account-menu"
              open={accountMenuOpen}
              onClose={handleAccountMenuClose}
              onClick={handleAccountMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* <MenuItem onClick={handleAccountMenuClose}>
                <Avatar /> Profile
              </MenuItem> */}
              <MyProfileItem />
              {/* <MenuItem onClick={handleAccountMenuClose}>
                <Avatar /> My account
              </MenuItem> */}
              <Divider />
              {/* <MenuItem onClick={handleAccountMenuClose}>
                <ListItemIcon>
                  <PersonAdd fontSize="small" />
                </ListItemIcon>
                Add another account
              </MenuItem>
              <MenuItem onClick={handleAccountMenuClose}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem> */}
              <LogoutItem signOut={signOut} />
            </Menu>

            <IconButton sx={{ ml: 1 }} onClick={toggleMode} color="inherit">
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Toolbar>
        </AppBar>


        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="persistent"
          anchor="left"
          open={drawerOpen}
        >
          <DrawerHeader>
            <Typography>Community Portal</Typography>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton component={Link} to={`/${muniId}/welcome`} selected={selectedIndex === 2} onClick={(event) => handleListItemClick(event, 2, "Welcome")}>
                <ListItemIcon>
                  <ChildCareIcon />
                </ListItemIcon>
                <ListItemText primary={"Welcome"} />
              </ListItemButton>
            </ListItem>

            {!config.hideDashboardTab && (
            <ListItem disablePadding >
              <ListItemButton component={Link} to={`/${muniId}/dashboard`} selected={selectedIndex === 1} onClick={(event) => handleListItemClick(event, 1, "Dashboard")}>
                <ListItemIcon>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary={"Dashboard"} />
              </ListItemButton>
            </ListItem>
            )}
          </List>

          <Divider />

            {!config.hideComplaintsTab && (
          <List>
            <ListItemButton onClick={() => handleMenuClick('complaints')}>
              <ListItemIcon>
                <CampaignIcon />
              </ListItemIcon>

              <ListItemText primary="Complaints" />
              {menuOpen['complaints'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={menuOpen['complaints']} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/submit-complaint`} selected={selectedIndex === 10} onClick={(event) => handleListItemClick(event, 10, "Submit Complaint")}>
                  <ListItemIcon>
                    <AnnouncementIcon />
                  </ListItemIcon>
                  <ListItemText primary={"Submit Complaint"} />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/complaint-list`} selected={selectedIndex === 11} onClick={(event) => handleListItemClick(event, 11, "Complaint List")}>
                  <ListItemIcon>
                    <ListAltIcon />
                  </ListItemIcon>
                  <ListItemText primary={"Complaint List"} />
                </ListItemButton>
              </List>
            </Collapse>
          </List>
            )}
          {!config.hidePermitsTab && (
            <List>
              <ListItemButton onClick={() => handleMenuClick('permits')}>
                <ListItemIcon>
                  <NightShelterIcon />
                </ListItemIcon>

                <ListItemText primary="Permits" />
                {menuOpen['permits'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              <Collapse in={menuOpen['permits']} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/submit-permit-application`} selected={selectedIndex === 12} onClick={(event) => handleListItemClick(event, 12, "Submit Permit Application")}>
                    <ListItemIcon>
                      <HistoryEduIcon />
                    </ListItemIcon>
                    <ListItemText primary={"Apply"} />
                  </ListItemButton>
                  <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/permit-application-list`} selected={selectedIndex === 13} onClick={(event) => handleListItemClick(event, 13, "Your Permit Applications")}>
                    <ListItemIcon>
                      <ListAltIcon />
                    </ListItemIcon>
                    <ListItemText primary={"Your Applications"} />
                  </ListItemButton>

                  <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/permit-list`} selected={selectedIndex === 15} onClick={(event) => handleListItemClick(event, 15, "Permit List")}>
                    <ListItemIcon>
                      <WorkspacePremiumIcon />
                    </ListItemIcon>
                    <ListItemText primary={"Permit List"} />
                  </ListItemButton>

                  {/* <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/voucher-list`} selected={selectedIndex === 16} onClick={(event) => handleListItemClick(event, 16, "Tax Vouchers")}>
                  <ListItemIcon>
                    <WorkspacePremiumIcon />
                  </ListItemIcon>
                  <ListItemText primary={"Tax Vouchers"} />
                </ListItemButton> */}

                  {!config.hidePayTab && (
                    <ListItemButton sx={{ pl: 4 }} component={Link} to={`/${muniId}/complaint-list`} selected={selectedIndex === 14} onClick={(event) => handleListItemClick(event, 14, "Payments List")}>
                      <ListItemIcon>
                        <PointOfSaleIcon />
                      </ListItemIcon>
                      <ListItemText primary={"Pay"} />
                    </ListItemButton>
                  )}
                </List>
              </Collapse>
            </List>
          )}
          {!config.hideTaxVouchersTab && (
            <List>
              <ListItemButton component={Link} to={`/${muniId}/voucher-list`} selected={selectedIndex === 16} onClick={(event) => handleListItemClick(event, 16, "Tax Vouchers")}>
                <ListItemIcon>
                  <WorkspacePremiumIcon />
                </ListItemIcon>
                <ListItemText primary={"Tax Vouchers"} />
              </ListItemButton>
            </List>
          )}


          {/* <List>
            <ListItem disablePadding>
              <ListItemButton component={Link} to={`/${muniId}/submit-complaint`} selected={selectedIndex === 4} onClick={(event) => handleListItemClick(event, 4, "Manage Account")}>
                <ListItemIcon>
                  <AnnouncementIcon />
                </ListItemIcon>
                <ListItemText primary={"Submit Complaint"} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to={`/${muniId}/complaint-list`} selected={selectedIndex === 5} onClick={(event) => handleListItemClick(event, 5, "Manage Account")}>
                <ListItemIcon>
                  <ListAltIcon />
                </ListItemIcon>
                <ListItemText primary={"Complaint List"} />
              </ListItemButton>
            </ListItem>
          </List> */}



        </Drawer>
        <Main open={drawerOpen}>
          <DrawerHeader />
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Welcome />} />
              <Route path="/login" element={<Navigate to={`/${muniId}/welcome`} replace />} />
              <Route path="/:muniName/" element={<Welcome />} />
              <Route path="/:muniName/login" element={<Navigate to={`/${muniId}/welcome`} replace />} />
              <Route path="/:muniName/welcome" element={<Welcome />} />
              <Route path="/:muniName/dashboard" element={<Dashboard />} />
              <Route path="/:muniName/submit-complaint" element={<SubmitComplaint />} />
              <Route path="/:muniName/complaint-list/*" element={<ComplaintList />} />
              <Route path="/:muniName/submit-permit-application" element={<SubmitPermitApplication />} />
              <Route path="/:muniName/permit-application-list/*" element={<PermitApplicationList />} />
              <Route path="/:muniName/permit-list/*" element={<PermitList />} />
              <Route path="/:muniName/voucher-list/*" element={<VoucherList />} />
              <Route path="/:muniName/smart-form/:id" element={<SmartForm />} />
              <Route path="/:muniName/municipay/return" element={<MuniciPayReturn />} />


              {/* <Route path="/:muniName/manageaccount" element={<ManageAccount />} /> */}
              {/* <Route path="/scraper" element={<Scraper />} />
            <Route path="/municipalities/*" element={<Municipalities />} />
            <Route path="/parcels/*" element={<Parcels />} />
            <Route path="/listings/*" element={<Listings />} />
            <Route path="/rentals/*" element={<Rentals />} />
            <Route path="/dwellingunits/*" element={<DwellingUnits />} />
            <Route path="/maps" element={<Maps />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/deduplication" element={<Deduplication />} />
            <Route path="/identification/*" element={<Identification />} /> */}
            </Routes>
          </Suspense>
        </Main>
      </Box>
    </Router>
  )
}

export default LayoutAuth